/**
 * Sistema de banco de dados local usando IndexedDB
 * Armazena dados para funcionamento offline
 */

const DB_NAME = 'OrderSystemDB'
const DB_VERSION = 1

// Stores (tabelas)
export const STORES = {
  CLIENTES: 'clientes',
  PRODUTOS: 'produtos',
  INDUSTRIAS: 'industrias',
  PEDIDOS_PENDENTES: 'pedidos_pendentes',
  SYNC_QUEUE: 'sync_queue',
  METADATA: 'metadata',
}

let dbInstance: IDBDatabase | null = null

/**
 * Inicializa o banco de dados IndexedDB
 */
export async function initDB(): Promise<IDBDatabase> {
  if (dbInstance) return dbInstance

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => reject(request.error)
    request.onsuccess = () => {
      dbInstance = request.result
      resolve(request.result)
    }

    request.onupgradeneeded = (event: any) => {
      const db = event.target.result

      // Store de clientes
      if (!db.objectStoreNames.contains(STORES.CLIENTES)) {
        const clientesStore = db.createObjectStore(STORES.CLIENTES, { keyPath: 'id' })
        clientesStore.createIndex('nome', 'nome', { unique: false })
        clientesStore.createIndex('cnpj', 'cnpj', { unique: false })
      }

      // Store de produtos
      if (!db.objectStoreNames.contains(STORES.PRODUTOS)) {
        const produtosStore = db.createObjectStore(STORES.PRODUTOS, { keyPath: 'id' })
        produtosStore.createIndex('nome', 'nome', { unique: false })
        produtosStore.createIndex('codigo', 'codigo', { unique: false })
      }

      // Store de indústrias
      if (!db.objectStoreNames.contains(STORES.INDUSTRIAS)) {
        const industriasStore = db.createObjectStore(STORES.INDUSTRIAS, { keyPath: 'id' })
        industriasStore.createIndex('nome', 'nome', { unique: false })
      }

      // Store de pedidos pendentes (offline)
      if (!db.objectStoreNames.contains(STORES.PEDIDOS_PENDENTES)) {
        const pedidosStore = db.createObjectStore(STORES.PEDIDOS_PENDENTES, { 
          keyPath: 'id',
          autoIncrement: true 
        })
        pedidosStore.createIndex('timestamp', 'timestamp', { unique: false })
        pedidosStore.createIndex('status', 'status', { unique: false })
      }

      // Store de fila de sincronização
      if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
        const syncStore = db.createObjectStore(STORES.SYNC_QUEUE, { 
          keyPath: 'id',
          autoIncrement: true 
        })
        syncStore.createIndex('type', 'type', { unique: false })
        syncStore.createIndex('timestamp', 'timestamp', { unique: false })
      }

      // Store de metadata (última sincronização, etc)
      if (!db.objectStoreNames.contains(STORES.METADATA)) {
        db.createObjectStore(STORES.METADATA, { keyPath: 'key' })
      }

      console.log('✅ IndexedDB inicializado com sucesso')
    }
  })
}

/**
 * Adiciona ou atualiza um registro
 */
export async function put<T>(storeName: string, data: T): Promise<void> {
  const db = await initDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readwrite')
    const store = transaction.objectStore(storeName)
    const request = store.put(data)

    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

/**
 * Adiciona múltiplos registros
 */
export async function putMany<T>(storeName: string, items: T[]): Promise<void> {
  const db = await initDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readwrite')
    const store = transaction.objectStore(storeName)
    
    let completed = 0
    items.forEach(item => {
      const request = store.put(item)
      request.onsuccess = () => {
        completed++
        if (completed === items.length) resolve()
      }
      request.onerror = () => reject(request.error)
    })

    if (items.length === 0) resolve()
  })
}

/**
 * Obtém um registro por ID
 */
export async function get<T>(storeName: string, id: string | number): Promise<T | undefined> {
  const db = await initDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readonly')
    const store = transaction.objectStore(storeName)
    const request = store.get(id)

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

/**
 * Obtém todos os registros de uma store
 */
export async function getAll<T>(storeName: string): Promise<T[]> {
  const db = await initDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readonly')
    const store = transaction.objectStore(storeName)
    const request = store.getAll()

    request.onsuccess = () => resolve(request.result || [])
    request.onerror = () => reject(request.error)
  })
}

/**
 * Remove um registro
 */
export async function remove(storeName: string, id: string | number): Promise<void> {
  const db = await initDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readwrite')
    const store = transaction.objectStore(storeName)
    const request = store.delete(id)

    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

/**
 * Limpa todos os registros de uma store
 */
export async function clear(storeName: string): Promise<void> {
  const db = await initDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readwrite')
    const store = transaction.objectStore(storeName)
    const request = store.clear()

    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

/**
 * Conta registros em uma store
 */
export async function count(storeName: string): Promise<number> {
  const db = await initDB()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readonly')
    const store = transaction.objectStore(storeName)
    const request = store.count()

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

/**
 * Salva metadata
 */
export async function setMetadata(key: string, value: any): Promise<void> {
  await put(STORES.METADATA, { key, value, updatedAt: Date.now() })
}

/**
 * Obtém metadata
 */
export async function getMetadata(key: string): Promise<any> {
  const result = await get<{ key: string; value: any }>(STORES.METADATA, key)
  return result?.value
}

/**
 * Verifica se os dados estão desatualizados
 */
export async function isDataStale(key: string, maxAgeMs: number = 3600000): Promise<boolean> {
  const lastSync = await getMetadata(`lastSync_${key}`)
  if (!lastSync) return true
  
  return Date.now() - lastSync > maxAgeMs
}

/**
 * Marca sincronização completa
 */
export async function markSynced(key: string): Promise<void> {
  await setMetadata(`lastSync_${key}`, Date.now())
}
